import moment from "moment";

import { PatientDetailButton } from "components/PatientDetailsButton/PatientDetailsButton";
import { Pill, PILL_COLORS } from "components/ui/Pill";

import {
  CaseTypePill,
  CdmStatusPill,
  MatchedCell,
} from "../components/CdmFinderCells/CdmFinderCells";

const formatDate = (date) => (date ? moment(date).format("DD-MM-YYYY") : "N/A");
const formatDateTime = (date) => (date ? moment(date).format("DD-MM-YYYY HH:mm") : "N/A");

const patientNameCol = {
  id: "patientName",
  header: "Name",
  width: "minmax(min-content, 1fr)",
  render: (row) => {
    const fullName = `${row.firstName || ""} ${row.lastName || ""}`.trim();
    return (
      <PatientDetailButton
        patientId={row.patientId}
        patientFullName={fullName}
      />
    );
  },
  getTooltipText: (row) =>
    `${row.firstName || ""} ${row.lastName || ""}`.trim(),
};

const dobCol = {
  id: "dateOfBirth",
  header: "Date of Birth",
  width: "130px",
  render: (row) => formatDate(row.dateOfBirth),
};

const getGenderColors = (gender) => {
  const g = (gender || "").toLowerCase();
  if (g === "male" || g === "m") return PILL_COLORS.genderMale;
  if (g === "female" || g === "f") return PILL_COLORS.genderFemale;
  return PILL_COLORS.genderUnknown;
};

const formatGender = (gender) =>
  gender ? gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase() : "";

const genderCol = {
  id: "gender",
  header: "Gender",
  width: "110px",
  render: (row) =>
    row.gender ? (
      <Pill {...getGenderColors(row.gender)}>{formatGender(row.gender)}</Pill>
    ) : (
      "N/A"
    ),
};

const getAuditCols = () => [
  {
    id: "accessDateTime",
    header: "Date accessed",
    width: "150px",
    render: (row) => formatDate(row.accessDateTime),
  },
  { ...patientNameCol },
  { ...dobCol },
  { ...genderCol },
  {
    id: "accessedBy",
    header: "Accessed by",
    width: "200px",
    render: (row) => {
      if (!row.staffFirstName && !row.staffLastName) return "N/A";
      return `${row.staffFirstName || ""} ${row.staffLastName || ""}`.trim();
    },
  },
  {
    id: "accessReason",
    header: "Access reason",
    width: "260px",
    render: (row) => row.accessReason || "-",
  },
  {
    id: "dailyInteractions",
    header: "Daily access count",
    width: "180px",
    render: (row) => row.dailyInteractions ?? "-",
  },
];

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "-";
  return `€${Number(value).toFixed(2)}`;
};

const getAppointmentStatusColors = (status) => {
  const s = (status || "").toUpperCase().replace(/_/g, " ");
  if (s === "COMPLETED") return PILL_COLORS.sentYes;
  if (s === "ARRIVED" || s === "IN CONSULTATION") return PILL_COLORS.docNew;
  if (s === "DUE") return PILL_COLORS.method;
  if (s === "CANCELLED" || s === "NO SHOW") return PILL_COLORS.reactionYes;
  if (s === "NO APPT") return PILL_COLORS.reactionUnknown;
  return PILL_COLORS.reactionUnknown;
};

const formatAppointmentStatus = (status) => {
  if (!status) return "No Appt";
  const formatted = status.replace(/_/g, " ");
  return formatted.charAt(0).toUpperCase() + formatted.slice(1).toLowerCase();
};

const formatConsultationType = (type) => {
  if (!type) return "-";
  const formatted = type.replace(/_/g, " ");
  return formatted.charAt(0).toUpperCase() + formatted.slice(1).toLowerCase();
};

const getPatientCols = () => [
  {
    id: "dateOfConsultation",
    header: "Date",
    width: "95px",
    render: (row) => formatDate(row.dateOfConsultation),
  },
  {
    id: "startTime",
    header: "Time",
    width: "55px",
    render: (row) => row.startTime ? moment(row.startTime).format("HH:mm") : "-",
  },
  {
    id: "consultationName",
    header: "Consultation",
    width: "minmax(80px, 130px)",
    render: (row) => row.consultationName || "-",
    getTooltipText: (row) => row.consultationName || "",
  },
  {
    ...patientNameCol,
    width: "minmax(90px, 140px)",
  },
  { ...dobCol, width: "95px" },
  { ...genderCol, width: "85px" },
  {
    id: "staffName",
    header: "Staff",
    width: "minmax(80px, 120px)",
    render: (row) => {
      if (!row.doctorFirstName && !row.doctorLastName) return "N/A";

      return `${row.doctorFirstName || ""} ${row.doctorLastName || ""}`.trim();
    },
    getTooltipText: (row) => {
      if (!row.doctorFirstName && !row.doctorLastName) return "";

      return `${row.doctorFirstName || ""} ${row.doctorLastName || ""}`.trim();
    },
  },
  {
    id: "appointmentStatus",
    header: "Status",
    width: "95px",
    render: (row) => (
      <Pill {...getAppointmentStatusColors(row.appointmentStatus)}>
        {formatAppointmentStatus(row.appointmentStatus)}
      </Pill>
    ),
  },
  {
    id: "consultationType",
    header: "Type",
    width: "85px",
    render: (row) => formatConsultationType(row.consultationType),
  },
  {
    id: "totalCost",
    header: "Cost",
    width: "65px",
    render: (row) => formatCurrency(row.totalCost),
  },
  {
    id: "totalDue",
    header: "Due",
    width: "65px",
    render: (row) => formatCurrency(row.totalDue),
  },
  {
    id: "prescription",
    header: "Prescription",
    width: "90px",
    render: (row) => {
      const value = row.prescription;
      if (value === "Y" || value === "Yes") {
        return <Pill {...PILL_COLORS.sentYes}>Yes</Pill>;
      }
      if (value === "N" || value === "No") {
        return <Pill {...PILL_COLORS.sentNo}>No</Pill>;
      }

      return value || "-";
    },
  },
  {
    id: "investigation",
    header: "Investigation",
    width: "100px",
    render: (row) => {
      const value = row.investigation;
      if (value === "Y" || value === "Yes") {
        return <Pill {...PILL_COLORS.sentYes}>Yes</Pill>;
      }
      if (value === "N" || value === "No") {
        return <Pill {...PILL_COLORS.sentNo}>No</Pill>;
      }

      return value || "-";
    },
  },
  {
    id: "referral",
    header: "Referral",
    width: "75px",
    render: (row) => {
      const value = row.referral;
      if (value === "Y" || value === "Yes") {
        return <Pill {...PILL_COLORS.sentYes}>Yes</Pill>;
      }
      if (value === "N" || value === "No") {
        return <Pill {...PILL_COLORS.sentNo}>No</Pill>;
      }

      return value || "-";
    },
  },
];

const getMedicationCols = () => [
  {
    id: "prescriptionDate",
    header: "Prescribed Date",
    width: "130px",
    render: (row) => formatDate(row.prescriptionDate),
  },
  { ...patientNameCol },
  { ...dobCol },
  { ...genderCol },
  {
    id: "tradeName",
    header: "Drug name",
    width: "410px",
    render: (row) => row.tradeName || "-",
  },
  {
    id: "manufacturer",
    header: "Manufacturer",
    width: "330px",
    render: (row) => row.manufacturer || "-",
  },
  {
    id: "form",
    header: "Form",
    width: "140px",
    render: (row) => {
      const v = row.form;
      if (!v) return "-";
      const formatted = v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
      return <Pill {...PILL_COLORS.method}>{formatted}</Pill>;
    },
  },
];

const getReferralCols = () => [
  {
    id: "dateSent",
    header: "Date Sent",
    width: "130px",
    render: (row) => formatDate(row.dateSent),
  },
  { ...patientNameCol },
  { ...dobCol },
  { ...genderCol },
  {
    id: "doctorName",
    header: "Doctor Name",
    width: "130px",
    render: (row) => {
      if (!row.doctorFirstName && !row.doctorSecondName) return "N/A";
      return `${row.doctorFirstName || ""} ${row.doctorSecondName || ""}`.trim();
    },
  },
  {
    id: "referralType",
    header: "Type",
    width: "250px",
    render: (row) => row.referralType || "-",
  },
];

const getLetterCols = () => [
  {
    id: "dateSent",
    header: "Date Sent",
    width: "130px",
    render: (row) => formatDate(row.dateSent),
  },
  { ...patientNameCol },
  { ...dobCol },
  { ...genderCol },
  {
    id: "doctorName",
    header: "Doctor Name",
    width: "130px",
    render: (row) => {
      if (!row.doctorFirstName && !row.doctorSecondName) return "N/A";
      return `${row.doctorFirstName || ""} ${row.doctorSecondName || ""}`.trim();
    },
  },
  {
    id: "letterType",
    header: "Letter Type",
    width: "130px",
    render: (row) => row.letterType || "-",
  },
  {
    id: "letterName",
    header: "Letter Name",
    width: "minmax(min-content, 1fr)",
    // Not yet returned by reports/get/letters (page/all/export) — backend
    // needs to project PatientLetter.name onto the report DTO. Blank until then.
    render: (row) => row.name || "-",
  },
];

const getImmunisationCols = () => [
  {
    id: "dateAdministered",
    header: "Administered Date",
    width: "150px",
    render: (row) => formatDate(row.dateAdministered),
  },
  { ...patientNameCol },
  { ...dobCol },
  { ...genderCol },
  {
    id: "vaccineName",
    header: "Vaccine Name",
    width: "280px",
    render: (row) => row.vaccineName || "-",
  },
  {
    id: "batchNo",
    header: "Batch No",
    width: "120px",
    render: (row) => row.batchNo || "-",
  },
  {
    id: "reaction",
    header: "Reaction",
    width: "120px",
    render: (row) => {
      const v = row.reaction;
      if (!v) return "-";
      const formatted = v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
      if (v === "Y" || v === "Yes") return <Pill {...PILL_COLORS.reactionYes}>{formatted}</Pill>;
      if (v === "N" || v === "No") return <Pill {...PILL_COLORS.reactionNo}>{formatted}</Pill>;
      return <Pill {...PILL_COLORS.reactionUnknown}>{formatted}</Pill>;
    },
  },
];

const getPaymentCols = () => [
  {
    id: "dueDate",
    header: "Created Date",
    width: "130px",
    render: (row) => formatDate(row.dueDate),
  },
  {
    id: "serviceName",
    header: "Service",
    width: "310px",
    render: (row) => row.serviceName || "-",
  },
  { ...patientNameCol },
  { ...dobCol },
  { ...genderCol },
  {
    id: "paymentStatus",
    header: "Payment Status",
    width: "140px",
    render: (row) => {
      const v = row.paymentStatus;
      if (!v) return "-";
      if (v === "Paid") return <Pill {...PILL_COLORS.sentYes}>{v}</Pill>;
      if (v === "Unpaid" || v === "Overdue") return <Pill {...PILL_COLORS.reactionYes}>{v}</Pill>;
      return <Pill {...PILL_COLORS.reactionUnknown}>{v}</Pill>;
    },
  },
  {
    id: "amountDue",
    header: "Amount",
    width: "100px",
    render: (row) => row.amountDue ?? "-",
  },
];

const getAppointmentCols = () => [
  {
    id: "startTime",
    header: "Appointment Date",
    width: "150px",
    render: (row) => formatDateTime(row.startTime),
  },
  { ...patientNameCol, header: "Patient Name" },
  { ...dobCol },
  {
    id: "serviceName",
    header: "Service",
    width: "310px",
    render: (row) => row.serviceName || "-",
  },
  { ...genderCol },
  {
    id: "doctorName",
    header: "Doctor Name",
    width: "130px",
    render: (row) => {
      if (!row.doctorFirstName && !row.doctorSecondName) return "N/A";
      return `${row.doctorFirstName || ""} ${row.doctorSecondName || ""}`.trim();
    },
  },
  {
    id: "appointmentStatus",
    header: "Appointment Status",
    width: "150px",
    render: (row) => {
      const v = row.appointmentStatus;
      if (!v) return "-";
      const normalized = v.replace(/_/g, " ");
      const formatted = normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
      const key = normalized.toLowerCase();
      if (key === "attended" || key === "completed") return <Pill {...PILL_COLORS.sentYes}>{formatted}</Pill>;
      if (key === "cancelled" || key === "no show") return <Pill {...PILL_COLORS.reactionYes}>{formatted}</Pill>;
      if (key === "scheduled" || key === "confirmed") return <Pill {...PILL_COLORS.docNew}>{formatted}</Pill>;
      return <Pill {...PILL_COLORS.reactionUnknown}>{formatted}</Pill>;
    },
  },
  {
    id: "cancellationReason",
    header: "Cancellation Reason",
    width: "200px",
    render: (row) => row.cancellationReason || row.cancellation_reason || "-",
  },
];

const getCdmFinderCols = () => [
  // Name is the only flexible column (0 min so it never forces the table wider
  // than its container); everything else is fixed or strictly capped.
  // Name is the only flexible column; everything else is tight so the whole
  // table fits on screen without horizontal scrolling.
  { ...patientNameCol, width: "minmax(120px, 1fr)" },
  { ...dobCol, width: "104px" },
  { ...genderCol, width: "90px" },
  {
    id: "age",
    header: "Age",
    width: "56px",
    render: (row) => row.age ?? "-",
  },
  {
    id: "medicalCardNumber",
    header: "Medical Card",
    width: "116px",
    render: (row) => row.medicalCardNumber || "-",
  },
  {
    id: "patientSchemeId",
    header: "Patient ID",
    width: "108px",
    render: (row) => row.patientSchemeId || "-",
  },
  {
    id: "ihi",
    header: "IHI",
    width: "96px",
    render: (row) => row.ihi || "-",
  },
  {
    id: "mobilePhone",
    header: "Mobile",
    width: "118px",
    render: (row) => row.mobilePhone || "-",
  },
  {
    id: "cdmStatus",
    header: "CDM Status",
    width: "150px",
    render: (row) => <CdmStatusPill status={row.cdmStatus} />,
  },
  {
    id: "caseType",
    header: "Case type",
    width: "104px",
    render: (row) => <CaseTypePill caseType={row.caseType} />,
  },
  {
    id: "lastOcf",
    header: "Last OCF",
    width: "96px",
    render: (row) =>
      row.lastOcfDate
        ? formatDate(row.lastOcfDate)
        : row.hadOcf
        ? "Yes"
        : "No",
  },
  {
    id: "matched",
    header: "Matched",
    // Strict max so the chips never push the table past its container; chips
    // wrap and truncate within this width.
    width: "minmax(0, 300px)",
    render: (row) => (
      <MatchedCell
        matchReasons={row.matchReasons}
        matchedConditions={row.matchedConditions}
        prescription={row.prescription}
        matchedLabs={row.matchedLabsRaw}
        matchedBaseline={row.matchedBaseline}
      />
    ),
  },
];

const formatReviewType = (value) => {
  if (!value) return "-";
  const normalized = value.replace(/_/g, " ").toLowerCase();

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const getCdmRecallCols = () => [
  { ...patientNameCol },
  { ...dobCol },
  { ...genderCol },
  {
    id: "age",
    header: "Age",
    width: "80px",
    render: (row) => row.age ?? "-",
  },
  {
    id: "medicalCardNumber",
    header: "Medical Card No.",
    width: "150px",
    render: (row) => row.medicalCardNumber || "-",
  },
  {
    id: "programme",
    header: "Programme",
    width: "120px",
    render: (row) => row.programme || "-",
  },
  {
    id: "reviewType",
    header: "Next review type",
    width: "160px",
    render: (row) => formatReviewType(row.reviewType),
  },
  {
    id: "nextReviewDueDate",
    header: "Next review due",
    width: "150px",
    render: (row) => formatDate(row.nextReviewDueDate),
  },
  {
    id: "overdue",
    header: "Overdue",
    width: "120px",
    render: (row) =>
      row.overdue ? (
        <Pill {...PILL_COLORS.reactionYes}>Overdue</Pill>
      ) : (
        <Pill {...PILL_COLORS.sentYes}>Due</Pill>
      ),
  },
];

const getCdmRegisterCols = () => [
  { ...patientNameCol, width: "minmax(0, 1fr)" },
  { ...dobCol },
  { ...genderCol },
  {
    id: "age",
    header: "Age",
    width: "80px",
    render: (row) => row.age ?? "-",
  },
  {
    id: "medicalCardNumber",
    header: "Medical Card No.",
    width: "150px",
    render: (row) => row.medicalCardNumber || "-",
  },
  {
    id: "diagnoses",
    header: "Diagnoses",
    width: "minmax(0, 420px)",
    render: (row) => {
      const list = (row.diagnoses || [])
        .map((d) => (d.name ? `${d.code} — ${d.name}` : d.code))
        .filter(Boolean);

      return list.length ? list.join(", ") : "-";
    },
  },
  {
    id: "privateHealthProvider",
    header: "Private insurer",
    width: "180px",
    render: (row) => row.privateHealthProvider || "-",
  },
  {
    id: "lastOcfDate",
    header: "Last OCF",
    width: "130px",
    render: (row) => formatDate(row.lastOcfDate),
  },
];

export const getColumns = (reportType) => {
  switch (reportType) {
    case "cdm-finder":
      return getCdmFinderCols();
    case "cdm-recall":
      return getCdmRecallCols();
    case "cdm-register":
      return getCdmRegisterCols();
    case "immunisations":
      return getImmunisationCols();
    case "prescriptions":
      return getMedicationCols();
    case "investigations":
      return getReferralCols();
    case "letters":
      return getLetterCols();
    case "accounting":
      return getPaymentCols();
    case "appointments":
      return getAppointmentCols();
    case "audit-log":
      return getAuditCols();
    default:
      return getPatientCols();
  }
};
